import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faWandMagicSparkles,
  faPaperPlane,
  faRotateLeft,
  faDownload,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { ConsultationService, sharedApiClient } from '../fetcher';
import { useNotifications, IdentityService, useAuth } from '@medprompt/shared';
import '@medprompt/shared/src/common/modals.css';
import '../detail/tabs/MessagingModals.css';

const ReferralLetterEditorModal = ({ consultation, patientData, onClose }) => {
  const [greeting, setGreeting] = useState('Dear Colleague,');
  const [letterBody, setLetterBody] = useState('');
  const [signature, setSignature] = useState('');
  const [isEditingGreeting, setIsEditingGreeting] = useState(false);
  const [isEditingSignature, setIsEditingSignature] = useState(false);
  const [clinicName, setClinicName] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [pendingContent, setPendingContent] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);
  const chatEndRef = useRef(null);

  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);
  const identityService = useMemo(() => new IdentityService(), []);
  const { addNotification } = useNotifications();
  const { user } = useAuth();

  const consultationId = consultation?.consultationId;
  const patient = patientData;
  const patientNhsId = patient?.nhsId || consultation?.nhsId;
  const patientDisplayName = patient?.name
    || [patient?.firstName, patient?.lastName].filter(Boolean).join(' ')
    || null;
  const storageKey = `medprompt_referral_letter_${consultationId}`;
  const userName = user?.name || '';

  useEffect(() => {
    const fetchClinicName = async () => {
      try {
        const clinics = await identityService.fetchClinics();
        const selectedId = sessionStorage.getItem('selectedClinicId');
        const clinic = clinics?.find(c => c.clinicId === selectedId) || clinics?.[0];
        setClinicName(clinic?.clinicName || '');
      } catch (error) {
        console.error('Failed to fetch clinic:', error);
      }
    };
    fetchClinicName();
  }, [identityService]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.greeting) setGreeting(draft.greeting);
        if (draft.letterBody) setLetterBody(draft.letterBody);
        if (draft.signature) {
          setSignature(draft.signature);
        } else if (userName) {
          setSignature(`Yours sincerely,\n${userName}`);
        }
        if (typeof draft.prompt === 'string') setPrompt(draft.prompt);
        if (Array.isArray(draft.chatHistory)) setChatHistory(draft.chatHistory);
        if (draft.pendingContent) setPendingContent(draft.pendingContent);
        if (draft.pendingMessage) setPendingMessage(draft.pendingMessage);
      } else if (userName) {
        setSignature(`Yours sincerely,\n${userName}`);
      }
    } catch (error) {
      console.error('Failed to load referral letter draft:', error);
      if (userName) {
        setSignature(`Yours sincerely,\n${userName}`);
      }
    }
    setIsInitialized(true);
  }, [storageKey, userName]);

  useEffect(() => {
    if (!isInitialized || !consultationId) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        greeting,
        letterBody,
        signature,
        prompt,
        chatHistory,
        pendingContent,
        pendingMessage,
      }));
    } catch (error) {
      console.error('Failed to save referral letter draft:', error);
    }
  }, [greeting, letterBody, signature, prompt, chatHistory, pendingContent, pendingMessage, isInitialized, consultationId, storageKey]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isGenerating, pendingContent]);

  // Models often emit literal "\n" / "\t" sequences inside the content string
  const normalizeLetterContent = (text) => {
    if (!text) return text;
    return text
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t');
  };

  const fullLetter = `${greeting}\n\n${letterBody}${letterBody ? '\n\n' : ''}${signature}`;

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating || !consultationId) return;

    const userPrompt = prompt.trim();
    setChatHistory(prev => [...prev, {
      type: 'user',
      content: userPrompt,
      timestamp: Date.now(),
    }]);
    setPrompt('');
    setIsGenerating(true);
    setPendingContent(null);
    setPendingMessage(null);

    try {
      const response = await consultationService.generateReferralLetter(
        consultationId,
        userPrompt,
        letterBody || null
      );

      if (response.error) {
        setChatHistory(prev => [...prev, {
          type: 'error',
          content: response.error,
          timestamp: Date.now(),
        }]);
      } else if (response.content) {
        setPendingContent(normalizeLetterContent(response.content));
        setPendingMessage(response.message);
      } else {
        setChatHistory(prev => [...prev, {
          type: 'error',
          content: 'No content was generated. Please try a different prompt.',
          timestamp: Date.now(),
        }]);
      }
    } catch (err) {
      console.error('Error generating referral letter:', err);
      setChatHistory(prev => [...prev, {
        type: 'error',
        content: 'Failed to generate referral letter. Please try again.',
        timestamp: Date.now(),
      }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptPending = () => {
    const contentBefore = letterBody;
    setLetterBody(pendingContent);
    setChatHistory(prev => [...prev, {
      type: 'assistant',
      content: 'Changes accepted',
      message: pendingMessage,
      contentSnapshot: contentBefore,
      timestamp: Date.now(),
    }]);
    setPendingContent(null);
    setPendingMessage(null);
  };

  const handleRejectPending = () => {
    setChatHistory(prev => [...prev, {
      type: 'system',
      content: 'Changes rejected',
      timestamp: Date.now(),
    }]);
    setPendingContent(null);
    setPendingMessage(null);
  };

  const handleRevert = (contentSnapshot) => {
    setLetterBody(contentSnapshot);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleCopyToClipboard = useCallback(() => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(fullLetter).then(() => {
        addNotification('copy-success', 'Copied to clipboard', { isError: false });
      }).catch(() => {
        addNotification('copy-error', 'Failed to copy to clipboard', { isError: true });
      });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = fullLetter;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        addNotification('copy-success', 'Copied to clipboard', { isError: false });
      } catch (err) {
        addNotification('copy-error', 'Failed to copy to clipboard', { isError: true });
      }
      document.body.removeChild(textArea);
    }
  }, [fullLetter, addNotification]);

  const handleDownloadPDF = () => {
    try {
      const dateStr = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      const escapeHtml = (str) =>
        str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Referral Letter</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 12pt;
      line-height: 1.7;
      color: #111;
      background: #fff;
    }
    .page {
      max-width: 700px;
      margin: 60px auto;
      padding: 0 40px;
    }
    .letter-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 40px;
      padding-bottom: 16px;
      border-bottom: 1px solid #ccc;
    }
    .clinic-name {
      font-size: 16pt;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
    .letter-meta {
      text-align: right;
      font-size: 10pt;
      color: #444;
      line-height: 1.6;
    }
    .letter-body {
      white-space: pre-wrap;
      word-break: break-word;
      font-size: 12pt;
      line-height: 1.8;
    }
    @media print {
      body { margin: 0; }
      .page { margin: 0; padding: 20mm 20mm; max-width: 100%; }
      @page { margin: 15mm; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="letter-header">
      <div class="clinic-name">${escapeHtml(clinicName || 'Your Practice')}</div>
      <div class="letter-meta">
        <div>${dateStr}</div>
        ${patientDisplayName ? `<div>${escapeHtml(patientDisplayName)}</div>` : ''}
        ${patientNhsId ? `<div>NHS: ${escapeHtml(patientNhsId)}</div>` : ''}
      </div>
    </div>
    <div class="letter-body">${escapeHtml(fullLetter)}</div>
  </div>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        addNotification('pdf-error', 'Pop-up blocked — please allow pop-ups for this site', { isError: true });
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();
      addNotification('pdf-success', 'Print dialog opened — choose "Save as PDF"', { isError: false });
    } catch (error) {
      console.error('Failed to open print view:', error);
      addNotification('pdf-error', 'Failed to open print view', { isError: true });
    }
  };

  const renderEditableTop = () => (
    <div className="message-greeting-section">
      {isEditingGreeting ? (
        <textarea
          className="greeting-input-editable"
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          onBlur={() => {
            setIsEditingGreeting(false);
            setGreeting(prev => prev.trim());
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsEditingGreeting(false);
          }}
          autoFocus
          rows={2}
          placeholder="Enter greeting / recipient details..."
          style={{ width: '100%', resize: 'vertical', minHeight: '48px' }}
        />
      ) : (
        <div
          className="greeting-text-editable"
          onClick={() => setIsEditingGreeting(true)}
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {greeting || 'Click to add greeting / recipient details...'}
          <div className="greeting-edit-tooltip">Click to edit greeting</div>
        </div>
      )}
    </div>
  );

  const renderEditableSignature = () => (
    <div className="message-signature" style={{ borderTop: '1px dashed #e0e0e0' }}>
      {isEditingSignature ? (
        <textarea
          className="greeting-input-editable"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          onBlur={() => {
            setIsEditingSignature(false);
            setSignature(prev => prev.trim());
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsEditingSignature(false);
          }}
          autoFocus
          rows={3}
          placeholder="Enter sign-off..."
          style={{ width: '100%', resize: 'vertical', minHeight: '64px' }}
        />
      ) : (
        <div
          className="greeting-text-editable"
          onClick={() => setIsEditingSignature(true)}
          style={{ whiteSpace: 'pre-wrap', padding: '12px' }}
        >
          {signature || 'Click to add sign-off...'}
          <div className="greeting-edit-tooltip">Click to edit sign-off</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container modal-animated"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '960px',
          maxWidth: '95vw',
          height: '85vh',
          maxHeight: '860px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="modal-header">
          <h2>Write Referral Letter</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {(patientDisplayName || patientNhsId) && (
          <div style={{ padding: '0 20px 12px' }}>
            <div className="patient-info-section" style={{ margin: 0 }}>
              <div className="patient-name">{patientDisplayName || 'Unknown Patient'}</div>
              {patientNhsId && (
                <div className="patient-contact-info">
                  <div className="contact-info-item">
                    <span>NHS: {patientNhsId}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          style={{
            flex: 1,
            display: 'flex',
            gap: '12px',
            padding: '0 20px 20px',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Letter compose */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: pendingContent ? '8px' : '0' }}>
            {pendingContent ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#6f42c1',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                    Review generated changes
                    {pendingMessage && (
                      <span style={{ opacity: 0.8, marginLeft: '8px' }}>— {pendingMessage}</span>
                    )}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleRejectPending}
                      style={{
                        padding: '5px 12px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleAcceptPending}
                      style={{
                        padding: '5px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Accept
                    </button>
                  </div>
                </div>

                <div style={{ flex: 1, display: 'flex', gap: '8px', minHeight: 0 }}>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #ddd',
                        fontSize: '12px',
                        color: '#666',
                        fontWeight: '500',
                      }}
                    >
                      Current
                    </div>
                    <textarea
                      value={letterBody}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        resize: 'none',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        backgroundColor: '#fafafa',
                        lineHeight: 1.5,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '2px solid #6f42c1',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        padding: '6px 10px',
                        backgroundColor: 'rgba(111, 66, 193, 0.1)',
                        borderBottom: '1px solid rgba(111, 66, 193, 0.3)',
                        fontSize: '12px',
                        color: '#6f42c1',
                        fontWeight: '500',
                      }}
                    >
                      Generated
                    </div>
                    <textarea
                      value={pendingContent}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        resize: 'none',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        lineHeight: 1.5,
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="message-content-section" style={{ flex: 1, minHeight: 0 }}>
                <label className="section-label">Letter Content</label>
                <div className="message-compose-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {renderEditableTop()}
                  <textarea
                    className="message-textarea-editable"
                    value={letterBody}
                    onChange={(e) => setLetterBody(e.target.value)}
                    placeholder="Describe what referral letter you want in the AI assistant, or type the letter body here..."
                    disabled={isGenerating}
                    style={{ flex: 1, minHeight: '180px' }}
                  />
                  {renderEditableSignature()}
                </div>
                <div className="character-count">{fullLetter.length} characters</div>
              </div>
            )}
          </div>

          {/* AI chat panel */}
          <div
            style={{
              width: '320px',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #ddd',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#fafafa',
            }}
          >
            <div
              style={{
                padding: '10px 14px',
                backgroundColor: '#6f42c1',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              AI Assistant
            </div>

            <div
              style={{
                flex: 1,
                overflow: 'auto',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {chatHistory.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#888',
                    fontSize: '13px',
                    padding: '20px 10px',
                  }}
                >
                  <div>Describe the referral letter you want and I&apos;ll draft it from this consultation.</div>
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    You can refine with follow-up prompts after accepting a draft.
                  </div>
                </div>
              )}

              {chatHistory.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    ...(msg.type === 'user'
                      ? {
                          backgroundColor: '#e3f2fd',
                          marginLeft: '20px',
                          alignSelf: 'flex-end',
                        }
                      : msg.type === 'error'
                        ? {
                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                            border: '1px solid rgba(220, 53, 69, 0.3)',
                            color: '#dc3545',
                          }
                        : msg.type === 'system'
                          ? {
                              backgroundColor: '#f8f9fa',
                              color: '#666',
                              textAlign: 'center',
                              fontStyle: 'italic',
                            }
                          : {
                              backgroundColor: 'white',
                              border: '1px solid #e0e0e0',
                              marginRight: '20px',
                            }),
                  }}
                >
                  {msg.type === 'user' && <div style={{ color: '#1976d2' }}>{msg.content}</div>}
                  {msg.type === 'error' && <div>{msg.content}</div>}
                  {msg.type === 'system' && <div>{msg.content}</div>}
                  {msg.type === 'assistant' && (
                    <>
                      {msg.message && (
                        <div
                          style={{
                            color: '#155724',
                            marginBottom: '6px',
                            padding: '4px 6px',
                            backgroundColor: 'rgba(40, 167, 69, 0.1)',
                            borderRadius: '4px',
                            fontSize: '11px',
                          }}
                        >
                          {msg.message}
                        </div>
                      )}
                      <div style={{ color: '#28a745', marginBottom: '6px' }}>Changes accepted</div>
                      {msg.contentSnapshot !== undefined && (
                        <button
                          onClick={() => handleRevert(msg.contentSnapshot)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 6px',
                            fontSize: '11px',
                            color: '#6c757d',
                            backgroundColor: 'transparent',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                        >
                          <FontAwesomeIcon icon={faRotateLeft} />
                          Revert
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}

              {isGenerating && (
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    marginRight: '20px',
                    fontSize: '13px',
                    color: '#6f42c1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Generating...
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            <div
              style={{
                padding: '12px',
                borderTop: '1px solid #ddd',
                backgroundColor: 'white',
              }}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. Draft a cardiology referral for chest pain..."
                  disabled={isGenerating}
                  rows={2}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '13px',
                    resize: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  style={{
                    padding: '10px 14px',
                    backgroundColor: isGenerating ? '#6c757d' : '#6f42c1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: !prompt.trim() || isGenerating ? 'not-allowed' : 'pointer',
                    opacity: !prompt.trim() ? 0.6 : 1,
                  }}
                >
                  <FontAwesomeIcon icon={isGenerating ? faSpinner : faPaperPlane} spin={isGenerating} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions" style={{ borderTop: '1px solid #eee', padding: '16px 20px' }}>
          <button className="cancel-button" onClick={onClose} disabled={isGenerating}>
            Cancel
          </button>
          <button
            className="send-message-copy-button"
            onClick={handleCopyToClipboard}
            disabled={!letterBody.trim()}
            title="Copy to clipboard"
          >
            <FontAwesomeIcon icon={faCopy} />
            <span>Copy</span>
          </button>
          <button
            className="send-message-download-button"
            onClick={handleDownloadPDF}
            disabled={!letterBody.trim()}
            title="Download as PDF"
          >
            <FontAwesomeIcon icon={faDownload} />
            <span>Download PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ReferralLetterEditorModal);
